import { BlogPost, User } from "../types";

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? '/api/v1'
  : 'https://api.flowblog.org/api/v1';
const AUTH_BASE_URL = process.env.NODE_ENV === 'development'
  ? ''
  : 'https://api.flowblog.org';

// Centralized fetch function for public endpoints
export async function fetchApi(url: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  return response;
}

// Centralized authenticated fetch function
export async function fetchApiWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };

  // Add CSRF token for state-changing requests
  const method = (options.method || 'GET').toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrfToken = await getCsrfToken();
    headers = {
      ...headers,
      'X-CSRF-Token': csrfToken,
    };
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (response.status === 403 && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    clearCsrfToken();
    throw new Error('CSRF token invalid');
  }

  if (response.status === 401) {
    throw new Error('Authentication failed');
  }

  return response;
}

// CSRF token management
let cachedCsrfToken: string | null = null;

export async function getCsrfToken(): Promise<string> {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }

  try {
    const response = await fetch(`${AUTH_BASE_URL}/auth/csrf-token`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to get CSRF token');
    }

    const data = await response.json();
    const token = data.csrf_token;
    if (!token || typeof token !== 'string') {
      throw new Error('CSRF token not provided in response');
    }
    cachedCsrfToken = token;
    return token;
  } catch (error) {
    throw new Error('Unable to get CSRF token');
  }
}


export interface AuthResponse {
  success?: boolean;
  user?: {
    id: string;
    username: string;
    email: string;
  };
  code: number;
  message: string;
}

export interface SignupData {
  name: string;
  username: string;
  email: string;
  password: string;
}

export interface LoginData {
  email: string;
  password: string;
}

// API Service Functions
export async function fetchHomeBlogs(): Promise<BlogPost[]> {
  try {
    const response = await fetchApi(`${API_BASE_URL}/home`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.blogs || [];
  } catch (error) {
    throw error;
  }
}

export async function fetchUserBlogs(userId: string, status: string = 'published'): Promise<BlogPost[]> {
  try {
    const response = await fetchApi(`${API_BASE_URL}/blogs?user=${userId}&status=${status}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.blogs || [];
  } catch (error) {
    throw error;
  }
}

export async function fetchUser(userId: string): Promise<User> {
  try {
    const response = await fetchApi(`${API_BASE_URL}/user/${userId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const user = await response.json();
    return user;
  } catch (error) {
    throw error;
  }
}

export async function fetchBlogBySlug(slug: string): Promise<BlogPost | null> {
  try {
    const response = await fetchApi(`${API_BASE_URL}/blogs/${slug}`);
    if (!response.ok) {
      if (response.status === 404) {
        return null; // Blog not found
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blog = await response.json();
    return blog;
  } catch (error) {
    throw error;
  }
}

export async function fetchDocumentByID(id: string): Promise<BlogPost | null> {
  try {
    const response = await fetchApiWithAuth(`${API_BASE_URL}/documents/${id}`);
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blog = await response.json();
    return blog;
  } catch (error) {
    throw error;
  }
}



// Auth API Functions
export async function signup(data: SignupData): Promise<AuthResponse> {
  try {
    const response = await fetchApiWithAuth(`${AUTH_BASE_URL}/auth/signup`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const resJson = await response.json();
    if (!response.ok) {
      return {
        code: response.status,
        message: resJson.error || resJson.message || 'Signup failed',
      };
    }

    // Update cached CSRF token if provided in response
    if (resJson.csrf_token) {
      cachedCsrfToken = resJson.csrf_token;
    }

    return {
      ...resJson,
      code: response.status,
      message: 'Signup successful',
    };
  } catch (error: any) {
    if (error.message === 'CSRF token invalid') {
      // Retry once with new CSRF token
      try {
        const response = await fetchApiWithAuth(`${AUTH_BASE_URL}/auth/signup`, {
          method: 'POST',
          body: JSON.stringify(data),
        });
        const resJson = await response.json();
        if (response.ok) {
          if (resJson.csrf_token) {
            cachedCsrfToken = resJson.csrf_token;
          }
          return {
            ...resJson,
            code: response.status,
            message: 'Signup successful',
          };
        }
      } catch (retryError) {
        // Fall through to generic error
      }
    }
    return {
      code: 500,
      message: 'Service unavailable',
    };
  }
}

export async function login(data: LoginData): Promise<AuthResponse> {
  try {
    const response = await fetchApiWithAuth(`${AUTH_BASE_URL}/auth/login`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    const resJson = await response.json();
    if (!response.ok) {
      return {
        code: response.status,
        message: resJson.error || resJson.message || 'Login failed',
      };
    }

    // Update cached CSRF token if provided in response
    if (resJson.csrf_token) {
      cachedCsrfToken = resJson.csrf_token;
    }

    return {
      ...resJson,
      code: response.status,
      message: 'Login successful',
    };
  } catch (error: any) {
    if (error.message === 'CSRF token invalid') {
      // Retry once with new CSRF token
      try {
        const response = await fetchApiWithAuth(`${AUTH_BASE_URL}/auth/login`, {
          method: 'POST',
          body: JSON.stringify(data),
        });
        const resJson = await response.json();
        if (response.ok) {
          if (resJson.csrf_token) {
            cachedCsrfToken = resJson.csrf_token;
          }
          return {
            ...resJson,
            code: response.status,
            message: 'Login successful',
          };
        }
      } catch (retryError) {
        // Fall through to generic error
      }
    }
    return {
      code: 500,
      message: 'Service unavailable',
    };
  }
}


export async function getCurrentUser(): Promise<User> {
  try {
    const response = await fetchApiWithAuth(`${AUTH_BASE_URL}/auth/me`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function logout(): Promise<void> {
  try {
    await fetchApiWithAuth(`${AUTH_BASE_URL}/auth/logout`, {
      method: 'POST',
    });
  } catch (error) {
    // Ignore logout errors
  } finally {
    // Clean up tokens and CSRF cache
    clearCsrfToken();
  }
}

export async function getBio(user_id: string): Promise<any> {
  const response = await fetchApiWithAuth(`${API_BASE_URL}/bios/${user_id}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch bio: ${response.status}`);
  }
  return await response.json();
}

export async function updateBio(user_id: string, content: string, status: string): Promise<any> {
  const response = await fetchApiWithAuth(`${API_BASE_URL}/bios/${user_id}`, {
    method: 'PUT',
    body: JSON.stringify({ content, status }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update bio: ${response.status}`);
  }
  return await response.json();
}

export async function updateDocument(blogPost: BlogPost): Promise<any> {
  const response = await fetchApiWithAuth(`${API_BASE_URL}/documents/${blogPost.id}`, {
    method: 'PUT',
    body: JSON.stringify(blogPost),
  });
  if (!response.ok) {
    throw new Error(`Failed to update document: ${response.status}`);
  }
  return await response.json();
}

export async function createDocument(blogPost: BlogPost): Promise<any> {
  const response = await fetchApiWithAuth(`${API_BASE_URL}/documents`, {
    method: 'POST',
    body: JSON.stringify(blogPost),
  });
  if (!response.ok) {
    throw new Error(`Failed to create document: ${response.status}`);
  }
  return await response.json();
}

function clearCsrfToken() {
  cachedCsrfToken = null;
}
